import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Activity, RefreshCw, Users, Eye, BarChart3
} from 'lucide-react';

const getStatusDotColor = (daysActive) => {
  if (daysActive >= 5) return 'bg-emerald-500';
  if (daysActive >= 2) return 'bg-amber-500';
  return 'bg-slate-300';
};

const getEngagementBadgeClass = (score) => {
  if (score >= 20) return 'bg-emerald-500 text-white';
  if (score >= 10) return 'bg-amber-500 text-white';
  return 'bg-slate-200 text-slate-700';
};

const formatLastVisit = (iso) =>
  iso
    ? new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

/**
 * "Activité Utilisateurs" tab — shows summary stats and per-user engagement.
 */
export default function ActivityTab({
  userActivity,
  loadingActivity,
  activityPeriod,
  setActivityPeriod,
  fetchUserActivity,
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-600" />
            Activité des Utilisateurs
          </CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={activityPeriod}
              onChange={(e) => setActivityPeriod(parseInt(e.target.value))}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
            >
              <option value={7}>7 derniers jours</option>
              <option value={14}>14 derniers jours</option>
              <option value={30}>30 derniers jours</option>
              <option value={90}>90 derniers jours</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUserActivity}
              disabled={loadingActivity}
            >
              <RefreshCw className={`w-4 h-4 ${loadingActivity ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loadingActivity ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : userActivity ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700">Utilisateurs Actifs</p>
                    <p className="text-2xl font-bold text-emerald-800">{userActivity.total_unique_users}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Eye className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">Pages Vues</p>
                    <p className="text-2xl font-bold text-blue-800">{userActivity.total_page_views}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-4 rounded-xl border border-violet-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm text-violet-700">Période</p>
                    <p className="text-2xl font-bold text-violet-800">{userActivity.period_days} jours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* User Activity Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-medium">Email</th>
                    <th className="text-center p-3 font-medium">Visites</th>
                    <th className="text-center p-3 font-medium">Jours Actifs</th>
                    <th className="text-center p-3 font-medium">Pages</th>
                    <th className="text-center p-3 font-medium">Score</th>
                    <th className="text-left p-3 font-medium">Dernière Visite</th>
                  </tr>
                </thead>
                <tbody>
                  {userActivity.users && userActivity.users.length > 0 ? (
                    userActivity.users.map((user) => (
                      <tr key={user.user_id || user.email} className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${getStatusDotColor(user.days_active)}`} />
                            <span className="font-medium">{user.email}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {user.total_visits}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                            {user.days_active} j
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-slate-600">{user.unique_pages}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${getEngagementBadgeClass(user.engagement_score)}`}>
                            {user.engagement_score}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-slate-600">
                          {formatLastVisit(user.last_visit)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        <Activity className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                        <p>Aucune activité enregistrée pour cette période</p>
                        <p className="text-xs mt-1">Les visites seront trackées après le déploiement</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            Impossible de charger les données d'activité
          </div>
        )}
      </CardContent>
    </Card>
  );
}
